import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './hooks/useAuthStore';
import { useRoleInfo, useModuleAccess } from './hooks/usePermissions';
import type { Module } from './config/permissions';
import { ToastProvider } from './components/common/Toast';
import LoginPage from './features/auth/components/LoginPage';
import DashboardPage from './features/dashboard/components/DashboardPage';
import ComponentsShowcase from './features/dashboard/components/ComponentsShowcase';
import AccountsPage from './features/accounts/components/AccountsPage';
import AccountDetailPage from './features/accounts/components/AccountDetailPage';
import AddEditAccountPage from './features/accounts/components/AddEditAccountPage';
import ContactsPage from './features/contacts/components/ContactsPage';
import ContactDetailPage from './features/contacts/components/ContactDetailPage';
import AddEditContactPage from './features/contacts/components/AddEditContactPage';
import CallsPage from './features/calls/components/CallsPage';
import CallManagementPageNew from './features/calls/components/CallManagementPage_NEW';
import CallCalendarView from './features/calls/components/CallCalendarView';
import CallTableView from './features/calls/components/CallTableView';
import CallBoardView from './features/calls/components/CallBoardView';
import CallDetailPage from './features/calls/components/CallDetailPage';
import ABMJourneyPage from './features/abm-journey/components/ABMJourneyPage';
import ABMJourneysListPage from './features/abm-journey/components/ABMJourneysListPage';
import MQLPage from './features/mql/components/MQLPage';
import MQLReadinessDashboard from './features/abm-journey/components/MQLReadinessDashboard';
import MQLDetailPage from './features/mql/components/MQLDetailPage';
import MQLEditPage from './features/mql/components/MQLEditPage';
import MagnetsPage from './features/magnets/components/MagnetsPage';
import MagnetDetailPage from './features/magnets/components/MagnetDetailPage';
import MagnetPortalPage from './features/magnet-portal/components/MagnetPortalPage';
import UserManagementPage from './features/users/components/UserManagementPage';
import SettingsPage from './features/settings/components/SettingsPage';
import ProspectingPage from './features/prospecting/components/ProspectingPage';
import EmailAgentPage from './features/communication/EmailAgentPage';
import VoiceAgentPage from './features/communication/VoiceAgentPage';
import AiCallingPage from './features/communication/AiCallingPage';
import BatchReviewPage from './features/prospecting/components/BatchReviewPage';
import AgentLogsPage from './features/prospecting/components/AgentLogsPage';
import NotificationsPage from './features/notifications/NotificationsPage';
import ReportsPage from './features/reports/components/ReportsPage';
import ActivityPage from './features/activity/components/ActivityPage';
import AppLayout from './components/layout/AppLayout';
import { ProspectingErrorBoundary } from './features/prospecting/components/ProspectingErrorBoundary';
import './styles/index.css';

// Map a path to the module that guards it (most-specific entries win).
const ROUTE_MODULE: { prefix: string; module: Module }[] = [
  { prefix: '/dashboard', module: 'dashboard' },
  { prefix: '/accounts', module: 'accounts' },
  { prefix: '/contacts', module: 'contacts' },
  { prefix: '/abm-journeys', module: 'abm_journey' },
  { prefix: '/calls', module: 'call_management' },
  { prefix: '/email-agent', module: 'call_management' },
  { prefix: '/voice-agent', module: 'call_management' },
  { prefix: '/ai-calling', module: 'call_management' },
  { prefix: '/magnet-portal', module: 'magnets' },
  { prefix: '/magnets', module: 'magnets' },
  { prefix: '/mql-readiness', module: 'mql_gate' },
  { prefix: '/mql', module: 'mql_gate' },
  { prefix: '/prospecting', module: 'prospecting' },
  { prefix: '/users', module: 'users' },
];

// Returns the guarding module for a path, or null for ungated/personal pages
// (/notifications, /settings, /components, /no-access).
const moduleForPath = (pathname: string): Module | null => {
  const hit = ROUTE_MODULE.find(
    (r) => pathname === r.prefix || pathname.startsWith(r.prefix + '/')
  );
  return hit ? hit.module : null;
};

// Where to send a user who lacks access to the page they hit — their first
// accessible page, in funnel order.
const LANDING_ORDER: { module: Module; path: string }[] = [
  { module: 'dashboard', path: '/dashboard' },
  { module: 'prospecting', path: '/prospecting' },
  { module: 'accounts', path: '/accounts' },
  { module: 'contacts', path: '/contacts' },
  { module: 'abm_journey', path: '/abm-journeys' },
  { module: 'call_management', path: '/calls' },
  { module: 'magnets', path: '/magnets' },
  { module: 'mql_gate', path: '/mql' },
  { module: 'users', path: '/users' },
];

function useLandingPath(): string {
  const canAccess = useModuleAccess();
  const hit = LANDING_ORDER.find((e) => canAccess(e.module));
  return hit ? hit.path : '/no-access';
}

function FullPageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

function NoAccessPage() {
  return (
    <div className="max-w-md mx-auto mt-16 text-center">
      <h1 className="text-xl font-semibold mb-2">No access</h1>
      <p className="text-muted-foreground">
        You don't have access to any modules yet. Please contact your administrator.
      </p>
    </div>
  );
}

// Protected + permission-aware route wrapper. Checks auth, then (when the path
// maps to a module) checks EFFECTIVE per-user access, redirecting to the user's
// landing page when they lack it.
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();
  const canAccess = useModuleAccess();
  const landing = useLandingPath();

  if (isLoading) return <FullPageSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const module = moduleForPath(location.pathname);
  if (module && !canAccess(module) && location.pathname !== landing) {
    return <Navigate to={landing} replace />;
  }

  return <AppLayout>{children}</AppLayout>;
}

// Extra guard for admin-only sub-pages, layered on top of ProtectedRoute.
function RequireModule({
  module,
  adminOnly = false,
  children,
}: {
  module: Module;
  adminOnly?: boolean;
  children: React.ReactNode;
}) {
  const canAccess = useModuleAccess();
  const { isAdmin, isABMManager } = useRoleInfo();
  const landing = useLandingPath();
  const allowed = adminOnly ? isAdmin || isABMManager : canAccess(module);

  if (!allowed) {
    return <Navigate to={landing} replace />;
  }

  return <>{children}</>;
}

// Sends a user to their first accessible page (or login).
function RootRedirect() {
  const { isAuthenticated, isLoading } = useAuthStore();
  const landing = useLandingPath();
  if (isLoading) return <FullPageSpinner />;
  return <Navigate to={isAuthenticated ? landing : '/login'} replace />;
}

function App() {
  const { getCurrentUser } = useAuthStore();

  useEffect(() => {
    // Check if user is authenticated on app load
    getCurrentUser();
  }, [getCurrentUser]);

  return (
    <ToastProvider>
      <BrowserRouter>
        <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/accounts"
          element={
            <ProtectedRoute>
              <AccountsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              <RequireModule module="reports">
                <ReportsPage />
              </RequireModule>
            </ProtectedRoute>
          }
        />

        <Route
          path="/accounts/new"
          element={
            <ProtectedRoute>
              <AddEditAccountPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/accounts/:id"
          element={
            <ProtectedRoute>
              <AccountDetailPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/accounts/:id/edit"
          element={
            <ProtectedRoute>
              <AddEditAccountPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/contacts"
          element={
            <ProtectedRoute>
              <ContactsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/contacts/new"
          element={
            <ProtectedRoute>
              <AddEditContactPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/contacts/:id"
          element={
            <ProtectedRoute>
              <ContactDetailPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/contacts/:id/edit"
          element={
            <ProtectedRoute>
              <AddEditContactPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/magnets"
          element={
            <ProtectedRoute>
              <MagnetsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/magnets/:id"
          element={
            <ProtectedRoute>
              <MagnetDetailPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/magnet-portal"
          element={
            <ProtectedRoute>
              <RequireModule module="magnets">
                <MagnetPortalPage />
              </RequireModule>
            </ProtectedRoute>
          }
        />

        <Route
          path="/calls"
          element={
            <ProtectedRoute>
              <CallManagementPageNew />
            </ProtectedRoute>
          }
        />

        <Route
          path="/calls/table"
          element={
            <ProtectedRoute>
              <CallTableView />
            </ProtectedRoute>
          }
        />

        <Route
          path="/calls/board"
          element={
            <ProtectedRoute>
              <CallBoardView />
            </ProtectedRoute>
          }
        />

        <Route
          path="/calls/calendar"
          element={
            <ProtectedRoute>
              <CallCalendarView />
            </ProtectedRoute>
          }
        />

        <Route
          path="/calls/:id"
          element={
            <ProtectedRoute>
              <CallDetailPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/abm-journeys"
          element={
            <ProtectedRoute>
              <ABMJourneysListPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/abm-journeys/new"
          element={
            <ProtectedRoute>
              <ABMJourneyPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/abm-journeys/:contactId"
          element={
            <ProtectedRoute>
              <ABMJourneyPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/mql"
          element={
            <ProtectedRoute>
              <MQLPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/mql-readiness"
          element={
            <ProtectedRoute>
              <MQLReadinessDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/mql/:id"
          element={
            <ProtectedRoute>
              <MQLDetailPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/mql/:id/edit"
          element={
            <ProtectedRoute>
              <MQLEditPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <UserManagementPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <NotificationsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/activity"
          element={
            <ProtectedRoute>
              <ActivityPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/components"
          element={
            <ProtectedRoute>
              <ComponentsShowcase />
            </ProtectedRoute>
          }
        />


        <Route
          path="/prospecting"
          element={
            <ProtectedRoute>
              <RequireModule module="prospecting">
                <ProspectingErrorBoundary>
                  <ProspectingPage />
                </ProspectingErrorBoundary>
              </RequireModule>
            </ProtectedRoute>
          }
        />

        <Route
          path="/prospecting/agent-logs"
          element={
            <ProtectedRoute>
              <RequireModule module="prospecting" adminOnly>
                <ProspectingErrorBoundary fallbackRoute="/prospecting">
                  <AgentLogsPage />
                </ProspectingErrorBoundary>
              </RequireModule>
            </ProtectedRoute>
          }
        />

        <Route
          path="/prospecting/batches/:id"
          element={
            <ProtectedRoute>
              <RequireModule module="prospecting">
                <ProspectingErrorBoundary fallbackRoute="/prospecting" fallbackLabel="Back to batches">
                  <BatchReviewPage />
                </ProspectingErrorBoundary>
              </RequireModule>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-calling"
          element={
            <ProtectedRoute>
              <AiCallingPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/voice-agent"
          element={
            <ProtectedRoute>
              <VoiceAgentPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/email-agent"
          element={
            <ProtectedRoute>
              <EmailAgentPage />
            </ProtectedRoute>
          }
        />

        {/* No-access fallback for users with zero accessible modules */}
        <Route
          path="/no-access"
          element={
            <ProtectedRoute>
              <NoAccessPage />
            </ProtectedRoute>
          }
        />

        {/* Default redirect → user's first accessible page */}
        <Route path="/" element={<RootRedirect />} />
        <Route path="*" element={<RootRedirect />} />
        </Routes>
      </BrowserRouter>
    </ToastProvider>
  );
}

export default App;
